import React from 'react';
import { Award, Clock, Users, Shield, TrendingUp, CheckCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const WhyChooseUs = () => {
  const reasons = [
    {
      icon: Award,
      title: 'Expert Legal Team',
      description: 'Our experienced attorneys and legal professionals ensure your application meets all USPTO requirements, maximizing approval chances and providing expert guidance throughout the entire process.',
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800'
    },
    {
      icon: Clock,
      title: 'Fast & Affordable',
      description: 'We offer competitive pricing starting at just $45 for trademark registration, with quick turnaround times and transparent pricing. No hidden fees, just honest service.',
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800'
    },
    {
      icon: Users,
      title: 'Full-Service Support',
      description: 'From initial search to registration and renewal, we provide comprehensive support throughout the entire process and beyond. Your success is our priority.',
      image: 'https://images.unsplash.com/photo-1556745753-b2904692b3cd?w=800'
    }
  ];

  const additionalFeatures = [
    'USPTO Certified Professionals',
    '24/7 Client Support',
    'Money-Back Guarantee',
    'Free Consultation',
    'Status Tracking Portal',
    'Lifetime Support'
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-[#FEF5F5] via-[#FDFEFE] to-[#FEF5F5] relative overflow-hidden">
      {/* Decorative large blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#C0392B]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#7F8C8D]/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#C0392B]/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50]">
            Why Choose Trademark Vector?
          </h2>
          <p className="text-xl text-[#7F8C8D] max-w-3xl mx-auto">
            We combine legal expertise with exceptional service to protect your brand and give you peace of mind
          </p>
        </div>

        {/* Main Reasons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <Card 
                key={index}
                className="border-[#7F8C8D]/20 hover:border-[#C0392B]/50 transition-all duration-500 hover:shadow-2xl card-lift group overflow-hidden"
              >
                {/* Animated Image */}
                <div className="relative h-48 overflow-hidden bg-gradient-to-br from-[#C0392B]/5 to-[#7F8C8D]/5">
                  <img 
                    src={reason.image}
                    alt={reason.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent opacity-60" />
                  {/* Floating Icon */}
                  <div className="absolute top-4 right-4 w-16 h-16 bg-white/90 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 icon-float">
                    <Icon className="w-8 h-8 text-[#C0392B] group-hover:scale-125 transition-transform duration-500" />
                  </div>
                </div>
                
                <CardContent className="pt-6 pb-8">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <h3 className="text-2xl font-bold text-[#2C3E50] group-hover:text-[#C0392B] transition-colors duration-300">{reason.title}</h3>
                    <p className="text-[#7F8C8D] leading-relaxed group-hover:text-[#2C3E50] transition-colors duration-300">{reason.description}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional Features Grid */}
        <div className="bg-gradient-to-br from-white to-[#FEF5F5] rounded-2xl shadow-xl p-8 md:p-12 border-2 border-[#C0392B]/10">
          <h3 className="text-2xl md:text-3xl font-bold text-[#2C3E50] text-center mb-10">
            Everything You Need for Success
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalFeatures.map((feature, index) => (
              <div key={index} className="flex items-center space-x-3 group cursor-pointer">
                <CheckCircle className="w-6 h-6 text-[#C0392B] flex-shrink-0 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300" />
                <span className="text-[#2C3E50] font-medium group-hover:text-[#C0392B] transition-colors duration-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;